import type {ViewConfiguration} from '../../../contexts/views/view-configuration'
import {boardInitialPayloadError, type BoardInitialPayload, isBoardInitialPayload} from './board-payload'
import {isViewConfiguration, viewConfigurationValidator} from './config-payload'

interface PayloadSource {
  /** Names the route in the reported message. */
  routeId: string
  /** The `name` of the reported error, which groups the reports for one surface. */
  errorName: string
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Reports a route payload that does not match its contract without breaking the synchronous
 * flow. An async rethrow is how an error reaches telemetry.
 */
export function reportPayloadMismatch(
  errorName: string,
  message: string,
  validatorError?: {instancePath: string; message: string},
): void {
  setTimeout(() => {
    const validationDetails = validatorError
      ? ` at ${validatorError.instancePath === '' ? 'payload' : validatorError.instancePath}: ${validatorError.message}`
      : ''
    const error = new Error(`${message}${validationDetails}`)
    error.name = errorName
    throw error
  })
}

/**
 * Undefined when the payload carries no configuration, or one that does not match the contract,
 * which is reported without the value it held.
 */
export function readEnvelopeConfiguration(
  raw: unknown,
  {routeId, errorName}: PayloadSource,
): ViewConfiguration | undefined {
  if (raw === undefined) return undefined
  if (isViewConfiguration(raw)) return raw

  reportPayloadMismatch(
    errorName,
    `${routeId} configuration failed validation`,
    viewConfigurationValidator.Errors(raw)[0],
  )
  return undefined
}

/** Whether a saved view route payload board matches the contract. A mismatch is reported. */
export function isValidBoardContent(raw: unknown, {routeId, errorName}: PayloadSource): raw is BoardInitialPayload {
  if (isBoardInitialPayload(raw)) return true

  reportPayloadMismatch(errorName, `${routeId} board payload failed validation`, boardInitialPayloadError(raw))
  return false
}

export type NewViewBoardPayload = {boardEnabled: false} | {boardEnabled: true; defaultConfiguration: ViewConfiguration}

/** A board with no valid default configuration reads as off, and the mismatch is reported. */
export function readNewViewBoard(raw: unknown, source: PayloadSource): NewViewBoardPayload {
  if (!isRecord(raw) || raw.boardEnabled !== true) return {boardEnabled: false}

  const rawConfiguration = raw.defaultConfiguration
  if (rawConfiguration === undefined) {
    reportPayloadMismatch(source.errorName, `${source.routeId} offers the board with no default configuration`)
    return {boardEnabled: false}
  }
  const defaultConfiguration = readEnvelopeConfiguration(rawConfiguration, source)
  if (!defaultConfiguration) return {boardEnabled: false}
  if (defaultConfiguration.layout !== 'board') {
    reportPayloadMismatch(source.errorName, `${source.routeId} default configuration is not a board`)
    return {boardEnabled: false}
  }

  return {boardEnabled: true, defaultConfiguration}
}
